import React from "react";
import { registerToastInstance } from "../utils/show_toast";

/**
 * ToastContext - 클래스/훅 양쪽 호환을 위한 React Context.
 * 기본 fallback `show`는 no-op (provider 없을 때 안전).
 */
export const ToastContext = React.createContext({ show: () => {} });

const MAX_TOASTS = 3;
const DEFAULT_DURATION = 3000;
const DEFAULT_ERROR_DURATION = 6000;

const VARIANT_BORDER = {
    error: "border-loss",
    success: "border-gain",
    info: "border-brand",
};

const VARIANT_ICON_COLOR = {
    error: "text-loss",
    success: "text-gain",
    info: "text-brand-light",
};

const VARIANT_ROLE = {
    error: "alert",
    success: "status",
    info: "status",
};

const ToastIcon = ({ type }) => {
    const cls = `h-4 w-4 flex-shrink-0 mt-0.5 ${VARIANT_ICON_COLOR[type] || VARIANT_ICON_COLOR.info}`;
    if (type === "error") {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cls}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
        );
    }
    if (type === "success") {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cls}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cls}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    );
};

class ToastProvider extends React.Component {
    constructor(props) {
        super(props);
        this.state = { toasts: [] };
        this.timers = new Map();
        this.nextId = 1;
        // 컨텍스트 value는 매 렌더 새 객체 생성을 피하기 위해 인스턴스에 보관
        this.contextValue = { show: this.show };
    }

    componentDidMount() {
        registerToastInstance(this);
    }

    componentWillUnmount() {
        registerToastInstance(null);
        this.timers.forEach((t) => clearTimeout(t));
        this.timers.clear();
    }

    show = (options) => {
        if (!options || !options.message) return;
        const type = options.type === "error" || options.type === "success" || options.type === "info"
            ? options.type
            : "info";
        const fallback = type === "error" ? DEFAULT_ERROR_DURATION : DEFAULT_DURATION;
        const duration = typeof options.duration === "number" ? options.duration : fallback;
        const id = this.nextId++;
        const toast = { id, type, message: options.message };

        this.setState((prev) => {
            let next = [...prev.toasts, toast];
            // 최대 3개 stack, 초과 시 oldest 제거 (해당 timer도 정리)
            while (next.length > MAX_TOASTS) {
                const dropped = next.shift();
                const t = this.timers.get(dropped.id);
                if (t) {
                    clearTimeout(t);
                    this.timers.delete(dropped.id);
                }
            }
            return { toasts: next };
        });

        if (duration > 0) {
            const timer = setTimeout(() => this.dismiss(id), duration);
            this.timers.set(id, timer);
        }
    };

    dismiss = (id) => {
        const t = this.timers.get(id);
        if (t) {
            clearTimeout(t);
            this.timers.delete(id);
        }
        this.setState((prev) => ({
            toasts: prev.toasts.filter((toast) => toast.id !== id),
        }));
    };

    renderToast = (toast) => (
        <div
            key={toast.id}
            role={VARIANT_ROLE[toast.type]}
            className={`pointer-events-auto bg-surface-card border-l-4 border border-surface-border ${VARIANT_BORDER[toast.type]} rounded-lg shadow-card animate-slide-up`}
        >
            <div className="flex items-start gap-2 px-4 py-3">
                <ToastIcon type={toast.type} />
                <p className="flex-1 min-w-0 text-content text-sm leading-snug break-words">
                    {toast.message}
                </p>
                <button
                    type="button"
                    onClick={() => this.dismiss(toast.id)}
                    aria-label="닫기"
                    className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] -m-2 p-2 rounded-md text-content-muted hover:text-content hover:bg-surface-hover transition-colors"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
        </div>
    );

    render() {
        const { toasts } = this.state;
        const errorToasts = toasts.filter((t) => t.type === "error");
        const politeToasts = toasts.filter((t) => t.type !== "error");
        return (
            <ToastContext.Provider value={this.contextValue}>
                {this.props.children}
                <div className="fixed right-4 z-50 flex flex-col gap-2 max-w-[calc(100vw-2rem)] sm:max-w-sm pointer-events-none bottom-[calc(4.5rem+env(safe-area-inset-bottom))] sm:bottom-4">
                    <div
                        aria-live="assertive"
                        aria-atomic="false"
                        className="flex flex-col gap-2"
                    >
                        {errorToasts.map(this.renderToast)}
                    </div>
                    <div
                        aria-live="polite"
                        aria-atomic="false"
                        className="flex flex-col gap-2"
                    >
                        {politeToasts.map(this.renderToast)}
                    </div>
                </div>
            </ToastContext.Provider>
        );
    }
}

export default ToastProvider;
